import {
  NOTIFICATION_CHANNEL_LABELS,
  NOTIFICATION_TYPE_LABELS,
  formatDateTime,
  formatRelative,
  type NotificationDto,
} from '@nova/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertOctagon, Bell, CheckCheck, Info, MessageSquare, Send } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../components/Button.js';
import { EmptyState } from '../components/EmptyState.js';
import { GlassPanel } from '../components/GlassPanel.js';
import { SkeletonPanel } from '../components/Skeleton.js';
import { ContentDrawer } from '../features/content/ContentDrawer.js';
import { api } from '../lib/api.js';
import { useAreaColor } from '../lib/area-color.js';
import { cn } from '../lib/cn.js';

const TYPE_ICON = {
  REMINDER: Bell,
  ESCALATION: AlertOctagon,
  INFO: Info,
  PREPARED_POST: Send,
  COMMENT: MessageSquare,
} as const;

const TYPE_TONE = {
  REMINDER: 'text-warning border-warning/25 bg-warning/[0.08]',
  ESCALATION: 'text-danger border-danger/30 bg-danger/[0.08]',
  INFO: 'text-accent border-accent/25 bg-accent/[0.07]',
  PREPARED_POST: 'text-violet border-violet/25 bg-violet/[0.08]',
  COMMENT: 'text-accent border-accent/25 bg-accent/[0.07]',
} as const;

export function NotificationsPage() {
  const queryClient = useQueryClient();
  const areaColor = useAreaColor();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ['notifications', { unreadOnly }],
    queryFn: () => api.get<NotificationDto[]>(`/notifications?unreadOnly=${unreadOnly}`),
    refetchInterval: 30_000,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const markRead = useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${id}/read`),
    onSuccess: invalidate,
  });

  const respond = useMutation({
    mutationFn: (id: string) =>
      api.post(`/notifications/${id}/respond`, { response: { ack: true } }),
    onSuccess: invalidate,
  });

  const markAllRead = useMutation({
    mutationFn: () => api.post('/notifications/read-all'),
    onSuccess: invalidate,
  });

  const unread = notifications.filter((n) => !n.readAt).length;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-ink text-xl">Varsler</h1>
          <p className="text-ink-soft mt-0.5 text-sm">
            {unread > 0 ? `${unread} uleste varsler` : 'Alt er lest.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setUnreadOnly((v) => !v)}>
            {unreadOnly ? 'Vis alle' : 'Vis kun uleste'}
          </Button>
          <Button
            size="sm"
            icon={<CheckCheck size={13} aria-hidden />}
            disabled={unread === 0}
            loading={markAllRead.isPending}
            onClick={() => markAllRead.mutate()}
          >
            Marker alle som lest
          </Button>
        </div>
      </header>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonPanel key={i} lines={2} />
          ))}
        </div>
      ) : notifications.length === 0 ? (
        <GlassPanel>
          <EmptyState
            title="Ingen varsler."
            description="Nova varsler deg når noe trenger oppmerksomheten din."
          />
        </GlassPanel>
      ) : (
        <ul className="space-y-2">
          {notifications.map((notification) => {
            const Icon = TYPE_ICON[notification.type];
            return (
              <li key={notification.id}>
                <GlassPanel
                  className={cn(
                    'transition',
                    !notification.readAt && 'border-accent/25 bg-panel/85',
                  )}
                  accentColor={notification.area?.color}
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={cn(
                        'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border',
                        TYPE_TONE[notification.type],
                      )}
                    >
                      <Icon size={15} aria-hidden />
                    </span>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                        <p className="text-ink text-sm">{notification.subject}</p>
                        <span
                          className="text-ink-soft text-[11px]"
                          title={formatDateTime(notification.sentAt)}
                        >
                          {formatRelative(notification.sentAt)}
                        </span>
                      </div>

                      <p className="text-ink-soft mt-1.5 text-sm leading-relaxed">
                        {notification.body}
                      </p>

                      <div className="mt-2.5 flex flex-wrap items-center gap-2">
                        <span className="border-hairline bg-raised/50 text-ink-soft rounded-full border px-2 py-0.5 text-[11px]">
                          {NOTIFICATION_TYPE_LABELS[notification.type]}
                        </span>
                        <span
                          className={cn(
                            'rounded-full border px-2 py-0.5 text-[11px]',
                            // A notification that never went out has to look
                            // different from one that did (07 — 2.1.6).
                            notification.delivered
                              ? 'border-hairline bg-raised/50 text-ink-soft'
                              : 'border-danger/40 bg-danger/10 text-danger',
                          )}
                          title={notification.deliveryDetail ?? undefined}
                        >
                          {NOTIFICATION_CHANNEL_LABELS[notification.channel]}
                          {notification.isDemo && ' (demo)'}
                          {!notification.delivered && ' — gikk ikke ut'}
                        </span>
                        {notification.area && (
                          <span
                            className="rounded-full border px-2 py-0.5 text-[11px]"
                            style={{
                              borderColor: `${notification.area.color}55`,
                              color: areaColor(notification.area.color),
                            }}
                          >
                            {notification.area.name}
                          </span>
                        )}

                        <span className="flex-1" />

                        {notification.contentItem && (
                          <Button size="sm" onClick={() => setOpenId(notification.contentItem!.id)}>
                            Åpne saken
                          </Button>
                        )}
                        {!notification.respondedAt && (
                          <Button
                            size="sm"
                            variant="primary"
                            loading={respond.isPending && respond.variables === notification.id}
                            onClick={() => respond.mutate(notification.id)}
                          >
                            Kvitter ut
                          </Button>
                        )}
                        {!notification.readAt && (
                          <Button
                            size="sm"
                            variant="ghost"
                            loading={markRead.isPending && markRead.variables === notification.id}
                            onClick={() => markRead.mutate(notification.id)}
                          >
                            Marker lest
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </GlassPanel>
              </li>
            );
          })}
        </ul>
      )}

      <ContentDrawer contentId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
